/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import { all, put, select, takeLatest, throttle } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { fetchWrapperFunc } from '../../utils';
import { getBettingUserApi2, getSystemConfig } from '../System/systemConfig';
import {
    countUser,
    fetchUser,
    resetPagingActions,
    setUserList,
    setUserListCount,
    setUserListStartPos,
    setUserNextEnabled,
    setUserPriorEnabled,
    setUserSearchString,
    USER_LIST_COUNT,
    USER_LIST_FETCH,
    USER_LIST_FIRST,
    USER_LIST_LAST,
    USER_LIST_PAGE_NEXT,
    USER_LIST_PAGE_PRIOR,
    USER_LIST_RESET_PAGING,
    USER_LIST_SEARCH
} from './actions';
import { UserListStore } from './reducer';
import { userListSelector } from './selectors';

const CLIENT_ID: number = getSystemConfig().clientId;

function* fetchUserGenerator() {
    const userApi = getBettingUserApi2();
    let state: UserListStore = yield select(userListSelector);
    yield* fetchWrapperFunc(
        () => userApi.searchActiveUsers(CLIENT_ID, state.startPos, state.slotSize, state.searchString),
        [(r) => setUserList(r)]
    );
}

function* countUserGenerator() {
    const userApi = getBettingUserApi2();
    let state: UserListStore = yield select(userListSelector);
    yield* fetchWrapperFunc(() => userApi.countActiveUsers(CLIENT_ID, state.searchString), [
        (r: number) => setUserListCount(r)
    ]);
    yield put(resetPagingActions());
}

const getLastPageStart = (count: number, slotSize: number) => {
    return Math.floor(count / slotSize) * slotSize;
};

function* resetPagingActionsGenerator() {
    let state: UserListStore = yield select(userListSelector);
    let priorEnabled: boolean = state.startPos > 0;
    let nextEnabled: boolean = state.startPos < getLastPageStart(state.count, state.slotSize);
    yield put(setUserNextEnabled(nextEnabled));
    yield put(setUserPriorEnabled(priorEnabled));
}

function* searchUsersGenerator(action: Action<string>) {
    let q = action.payload;

    yield put(setUserSearchString(q));
    yield put(fetchUser());
    yield put(setUserListStartPos(0));
    yield put(countUser());
}

function* pageNextUsersGenerator() {
    let state: UserListStore = yield select(userListSelector);

    let newStart: number = state.startPos + state.slotSize;

    if (newStart <= state.count) {
        yield put(setUserListStartPos(newStart));
        yield put(resetPagingActions());
        yield put(fetchUser());
    }
}

function* pagePriorUsersGenerator() {
    let state: UserListStore = yield select(userListSelector);

    let newStart: number = state.startPos - state.slotSize;

    if (newStart >= 0) {
        yield put(setUserListStartPos(newStart));
        yield put(resetPagingActions());
        yield put(fetchUser());
    }
}

function* pageLastUsersGenerator() {
    let state: UserListStore = yield select(userListSelector);

    let newStart: number = getLastPageStart(state.count, state.slotSize);

    yield put(setUserListStartPos(newStart));
    yield put(resetPagingActions());
    yield put(fetchUser());
}

function* pageFirstUsersGenerator() {
    let newStart: number = 0;

    yield put(setUserListStartPos(newStart));
    yield put(resetPagingActions());
    yield put(fetchUser());
}

function* watchUserListActions() {
    yield takeLatest(USER_LIST_COUNT, countUserGenerator);
    yield takeLatest(USER_LIST_FETCH, fetchUserGenerator);
    yield takeLatest(USER_LIST_FIRST, pageFirstUsersGenerator);
    yield takeLatest(USER_LIST_LAST, pageLastUsersGenerator);
    yield takeLatest(USER_LIST_PAGE_PRIOR, pagePriorUsersGenerator);
    yield takeLatest(USER_LIST_PAGE_NEXT, pageNextUsersGenerator);
    yield takeLatest(USER_LIST_RESET_PAGING, resetPagingActionsGenerator);
    yield throttle(500, USER_LIST_SEARCH, searchUsersGenerator);
}

export function* userListSagas() {
    yield all([watchUserListActions()]);
}
